import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { CalendarDays, RefreshCw } from "lucide-react"
import { useEffect, useId } from "react"
import { z } from "zod"

import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { EmptyState } from "@/components/shared/empty-state"
import { EventStatusBadge } from "@/components/shared/event-status-badge"
import { GuildEventRelativeTime } from "@/components/shared/guild-event-relative-time"
import { RouteErrorBoundary } from "@/components/shared/route-error-boundary"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { GridPageSkeleton } from "@/components/ui/page-skeletons"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  deriveLifecycleStatus,
  getRoleLabel,
  getRsvpLabel,
} from "@/core/guild-events/labels"
import { useEffectiveIsAdmin } from "@/lib/admin-view-store"
import { formatGuildEventDateTimeWithTimezone } from "@/lib/guild-date-format"
import { type GuildEventRecord, guildEventsApi } from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"

const MEMBER_PAGE_SIZE = 12

export const MEMBER_EVENT_DEFAULT_BUCKET = "active"

type MemberEventBucket = "active" | "archived" | "cancelled"

export function normalizeMemberEventSearch(
  bucket: string | undefined,
  page: number | undefined,
): { bucket: MemberEventBucket; page: number; shouldReplace: boolean } {
  if (bucket === "archived" || bucket === "cancelled" || bucket === "active") {
    return { bucket, page: page ?? 1, shouldReplace: false }
  }

  return {
    bucket: MEMBER_EVENT_DEFAULT_BUCKET,
    page: 1,
    shouldReplace: bucket !== undefined,
  }
}

const searchSchema = z.object({
  state: z.enum(["all", "attending", "tentative", "not_attending"]).optional(),
  bucket: z
    .enum(["all", "upcoming", "past", "active", "archived", "cancelled"])
    .optional(),
  page: z.coerce.number().int().min(1).optional(),
})

export const Route = createFileRoute("/_authed/events")({
  validateSearch: searchSchema,
  component: MemberEventsListRoute,
  errorComponent: RouteErrorBoundary,
})

function MemberEventsListRoute() {
  const { auth } = Route.useRouteContext()
  const canOpenEventDetails = useEffectiveIsAdmin(auth.user?.isAdmin ?? false)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const state = search.state ?? "all"
  const {
    bucket,
    page,
    shouldReplace: shouldNormalizeBucket,
  } = normalizeMemberEventSearch(search.bucket, search.page)

  const listQuery = useQuery({
    queryKey: guildEventsKeys.memberList({ state, bucket, page }),
    queryFn: () =>
      guildEventsApi.listMemberEvents({
        state,
        bucket,
        page,
        pageSize: MEMBER_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  })

  const items = listQuery.data?.items ?? []
  const total = listQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / MEMBER_PAGE_SIZE))

  const safePage = Math.min(page, pageCount)

  useEffect(() => {
    if (shouldNormalizeBucket || page !== safePage) {
      void navigate({
        search: (prev) => ({
          ...prev,
          bucket,
          page: shouldNormalizeBucket ? undefined : safePage,
        }),
        replace: true,
      })
    }
  }, [bucket, navigate, page, safePage, shouldNormalizeBucket])

  if (listQuery.isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading events">
        <GridPageSkeleton />
      </div>
    )
  }

  const isRefreshing = listQuery.isFetching
  const refreshEvents = () => listQuery.refetch()

  return (
    <div
      className="grid gap-4 md:gap-5"
      aria-busy={isRefreshing ? "true" : undefined}
    >
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          kicker={
            <SectionKicker icon={<CalendarDays />}>Guild Events</SectionKicker>
          }
          title="Guild events and your RSVP status"
          description="Browse active events, archived records, and cancelled events."
          actions={
            <Button
              type="button"
              variant="outline"
              onClick={() => void refreshEvents()}
              disabled={isRefreshing}
            >
              <RefreshCw
                className={`size-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          }
        />
      </Card>

      <Card>
        <CardContent className="grid gap-3 pt-5">
          <Tabs
            value={bucket}
            onValueChange={(next) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  bucket: next as typeof bucket,
                  page: undefined,
                }),
                replace: true,
              })
            }}
          >
            <TabsList aria-label="Event collection" variant="line">
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="archived">Archive</TabsTrigger>
              <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
            </TabsList>
          </Tabs>
          <FilterSelect
            label="My RSVP"
            value={state}
            options={[
              ["all", "All"],
              ["attending", "Attending"],
              ["tentative", "Tentative"],
              ["not_attending", "Not Attending"],
            ]}
            onChange={(next) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  state: next === "all" ? undefined : (next as typeof state),
                  page: undefined,
                }),
                replace: true,
              })
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-2 pt-5">
          {listQuery.isError ? (
            <StatusAlert tone="danger">
              <div className="flex items-center justify-between gap-3">
                <span>Failed to load events. Please try again.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void refreshEvents()}
                  disabled={isRefreshing}
                >
                  <RefreshCw
                    className={cn("size-3.5", isRefreshing && "animate-spin")}
                  />
                  Retry
                </Button>
              </div>
            </StatusAlert>
          ) : null}
          {!listQuery.isError && items.length === 0 ? (
            <MemberEventsEmptyState
              presentation={getMemberEventsEmptyPresentation({ state, bucket })}
            />
          ) : null}
          {items.map((event) => (
            <MemberEventRow
              key={event.id}
              event={event}
              canOpenEventDetails={canOpenEventDetails}
            />
          ))}
          {!listQuery.isError && items.length > 0 ? (
            <MemberEventPagination
              safePage={safePage}
              pageCount={pageCount}
              totalCount={total}
              onPageChange={(nextPage) => {
                void navigate({
                  search: (prev) => ({ ...prev, page: nextPage }),
                  replace: true,
                })
              }}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

export function MemberEventRow({
  event,
  canOpenEventDetails,
}: {
  event: GuildEventRecord
  canOpenEventDetails: boolean
}) {
  const presentation = getMemberEventRowPresentation(event)

  return (
    <Link
      to="/events/$eventId"
      params={{ eventId: event.id }}
      disabled={!canOpenEventDetails}
      className={cn(
        "grid gap-3 rounded-xl border border-border/70 bg-card/50 p-3 transition-colors hover:bg-card/80 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(10rem,0.8fr)] md:items-center",
        (event.status === "cancelled" || event.status === "archived") &&
          "opacity-50",
      )}
    >
      <div className="grid gap-1">
        <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          When
        </div>
        <div className="text-sm font-medium">{presentation.when}</div>
        <GuildEventRelativeTime startAt={event.startAt} />
      </div>
      <div className="grid min-w-0 gap-1">
        <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          What
        </div>
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold">{event.title}</span>
          <EventStatusBadge status={event.status} />
        </div>
        <div className="text-xs text-muted-foreground">{presentation.what}</div>
        {event.description ? (
          <div className="line-clamp-1 text-xs text-muted-foreground">
            {event.description}
          </div>
        ) : null}
      </div>
      <div className="grid gap-1">
        <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          State
        </div>
        <div className="text-sm font-medium">{presentation.state}</div>
      </div>
    </Link>
  )
}

export function getMemberEventRowPresentation(
  event: GuildEventRecord,
  now = new Date(),
) {
  const lifecycleStatus =
    event.status === "scheduled" || event.status === "closed"
      ? deriveLifecycleStatus(
          {
            ...event,
            startAt: new Date(event.startAt),
            signupCloseAt:
              event.eventKind === "raid" ? new Date(event.signupCloseAt) : null,
          },
          now,
        )
      : event.status
  const rsvp = event.memberRsvpState
    ? `Your RSVP: ${getRsvpLabel(event.memberRsvpState)}`
    : undefined
  const state =
    lifecycleStatus === "started"
      ? rsvp
        ? `Started · ${rsvp}`
        : "Started"
      : lifecycleStatus === "cancelled"
        ? rsvp
          ? `Cancelled · ${rsvp}`
          : "Cancelled"
        : lifecycleStatus === "archived"
          ? rsvp
            ? `Archived · RSVP unavailable · ${rsvp}`
            : "Archived · RSVP unavailable"
          : lifecycleStatus === "announcement_missing"
            ? rsvp
              ? `RSVP unavailable · ${rsvp}`
              : "RSVP unavailable"
            : lifecycleStatus === "closed"
              ? rsvp
                ? `Signups closed · ${rsvp}`
                : "Signups closed"
              : event.rsvpEnabled
                ? (rsvp ??
                  (event.eventKind === "raid"
                    ? "No role selected - Use discord to RSVP"
                    : "RSVP needed"))
                : "RSVPs not enabled"

  return {
    when: formatGuildEventDateTimeWithTimezone(event.startAt),
    what:
      event.eventKind === "raid"
        ? `Raid · Roles: ${event.eventRoles.map(getRoleLabel).join(", ")}`
        : "Attendance · Guild-wide RSVP",
    state,
  }
}

type MemberEventsEmptyPresentation = {
  title: string
  description: string
}

function MemberEventsEmptyState({
  presentation,
}: {
  presentation: MemberEventsEmptyPresentation
}) {
  return (
    <EmptyState
      icon={<CalendarDays className="size-5" />}
      title={presentation.title}
      description={presentation.description}
      tone="muted"
    />
  )
}

export function getMemberEventsEmptyPresentation({
  state,
  bucket,
}: {
  state: string
  bucket: string
}): MemberEventsEmptyPresentation {
  const collection =
    bucket === "archived"
      ? "archived events"
      : bucket === "cancelled"
        ? "cancelled events"
        : "active events"

  if (state === "all") {
    return {
      title: `No ${collection}`,
      description: `There are no ${collection} to show.`,
    }
  }

  return {
    title: `No matching ${collection}`,
    description: `No ${collection} match your RSVP filter.`,
  }
}
function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<[string, string]>
  onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <div className="grid gap-2">
      <label
        htmlFor={id}
        className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
      >
        {label}
      </label>
      <Select value={value} onValueChange={(next) => onChange(next ?? "")}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([optionValue, optionLabel]) => (
            <SelectItem key={optionValue} value={optionValue}>
              {optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function MemberEventPagination({
  safePage,
  pageCount,
  totalCount,
  onPageChange,
}: {
  safePage: number
  pageCount: number
  totalCount: number
  onPageChange: (page: number) => void
}) {
  return (
    <div className="mt-2 flex items-center justify-between">
      <div className="text-xs text-muted-foreground">
        Page {safePage} of {pageCount} ({totalCount} total)
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={safePage <= 1}
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={safePage >= pageCount}
          onClick={() => onPageChange(Math.min(pageCount, safePage + 1))}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
